import React, { useMemo } from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import { ActiveVisitor, RealtimeEvent } from '../hooks/useAnalyticsWebSocket';
import { GeolocationInsights } from './GeolocationInsights';

interface RealtimeChartsProps {
    events: RealtimeEvent[];
    visitors: ActiveVisitor[];
}

const COLORS = {
    primary: '#2563eb',
    secondary: '#10b981',
    accent: '#8b5cf6',
    warning: '#f59e0b',
    danger: '#ef4444',
    background: '#ffffff',
    text: '#737373',
    grid: '#f3f4f6'
};

export const RealtimeCharts: React.FC<RealtimeChartsProps> = ({ events, visitors }) => {
    // 1. Sessions Over Time (Last 30 minutes in 2-minute intervals)
    const sessionsTimeline = useMemo(() => {
        const data = [];
        const now = new Date();
        for (let i = 14; i >= 0; i--) {
            const time = new Date(now.getTime() - i * 2 * 60 * 1000);
            const timeLabel = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            // Count events in this window - REAL DATA
            const count = events.filter(e => {
                const eTime = new Date(e.timestamp);
                return eTime >= new Date(time.getTime() - 2 * 60 * 1000) && eTime <= time;
            }).length;

            data.push({
                time: timeLabel,
                sessions: count,
            });
        }
        return data;
    }, [events]);

    // 2. Conversion Breakdown (Real funnel based on platform events)
    const conversionData = useMemo(() => {
        const boardViews = events.filter(e => e.type === 'board.viewed').length;
        const channelConnections = events.filter(e => e.type === 'channel.connected').length;
        const subscriptions = events.filter(e => e.type === 'subscription.created' || e.type === 'user.signup').length;

        return [
            { name: 'Board Views', value: boardViews, color: COLORS.primary },
            { name: 'Channel Syncs', value: channelConnections, color: COLORS.accent },
            { name: 'Conversions', value: subscriptions, color: COLORS.secondary },
        ];
    }, [events]);

    // 3. Device Types Breakdown
    const deviceData = useMemo(() => {
        const types: Record<string, number> = { desktop: 0, mobile: 0, tablet: 0 };
        visitors.forEach(v => {
            const type = v.deviceType || 'desktop';
            types[type] = (types[type] || 0) + 1;
        });

        return [
            { name: 'Desktop', value: types.desktop, color: COLORS.primary },
            { name: 'Mobile', value: types.mobile, color: COLORS.secondary },
            { name: 'Tablet', value: types.tablet, color: COLORS.accent },
        ];
    }, [visitors]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Sessions Over Time */}
            <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col h-[320px] shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex flex-col">
                        <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-900">Activity Timeline</h3>
                        <span className="text-[10px] text-neutral-400 font-medium">Live traffic last 30 minutes</span>
                    </div>
                    <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-100">Live</span>
                </div>
                <div className="flex-1 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={sessionsTimeline}>
                            <defs>
                                <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.15} />
                                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                            <XAxis
                                dataKey="time"
                                fontSize={10}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: COLORS.text, fontWeight: 500 }}
                                interval={2}
                            />
                            <YAxis
                                fontSize={10}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: COLORS.text, fontWeight: 500 }}
                                allowDecimals={false}
                            />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #f3f4f6', borderRadius: '12px', fontSize: '11px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                itemStyle={{ color: COLORS.primary, fontWeight: 600 }}
                            />
                            <Area
                                type="monotone"
                                dataKey="sessions"
                                stroke={COLORS.primary}
                                fillOpacity={1}
                                fill="url(#colorSessions)"
                                strokeWidth={3}
                                animationDuration={1000}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Conversion Rate Breakdown */}
            <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col h-[320px] shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex flex-col">
                        <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-900">Success Funnel</h3>
                        <span className="text-[10px] text-neutral-400 font-medium">Conversion event summary</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 text-emerald-700">
                        <span className="text-xs font-bold">
                            {conversionData[0].value > 0 ? ((conversionData[2].value / conversionData[0].value) * 100).toFixed(1) : '0.0'}%
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-tighter">Rate</span>
                    </div>
                </div>
                <div className="flex-1 flex flex-col justify-center gap-6">
                    {conversionData.map((item) => {
                        const maxVal = Math.max(...conversionData.map(d => d.value)) || 1;
                        return (
                            <div key={item.name} className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{item.name}</span>
                                    <span className="text-neutral-900 font-bold tabular-nums">{item.value}</span>
                                </div>
                                <div className="w-full h-2.5 bg-neutral-50 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-1000 shadow-sm"
                                        style={{
                                            width: `${(item.value / maxVal) * 100}%`,
                                            backgroundColor: item.color,
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                    {conversionData.every(d => d.value === 0) && (
                        <div className="text-center py-4 bg-neutral-50 rounded-xl border border-dashed border-neutral-200">
                            <span className="text-[10px] text-neutral-400 uppercase font-bold tracking-widest">Awaiting interaction data...</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Hardware Distribution */}
            <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col h-[320px] shadow-sm">
                <div className="mb-6">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-900">Hardware Distribution</h3>
                    <span className="text-[10px] text-neutral-400 font-medium">Active visitor device types</span>
                </div>
                <div className="flex-1 flex items-center justify-center relative">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={deviceData.filter(d => d.value > 0)}
                                cx="50%"
                                cy="50%"
                                innerRadius={65}
                                outerRadius={90}
                                paddingAngle={10}
                                dataKey="value"
                                animationDuration={1000}
                                stroke="none"
                            >
                                {deviceData.filter(d => d.value > 0).map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #f3f4f6', borderRadius: '12px', fontSize: '11px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-3xl font-black text-neutral-900 leading-none">{visitors.length}</span>
                        <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mt-1">Active</span>
                    </div>
                </div>
            </div>

            {/* Activity Hotspots */}
            <div className="bg-white border border-neutral-100 rounded-2xl p-6 flex flex-col h-[320px] shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex flex-col">
                        <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-900">Activity Hotspots</h3>
                        <span className="text-[10px] text-neutral-400 font-medium">Top cities by live session count</span>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto space-y-3 pr-1 custom-scrollbar">
                    {Object.entries(
                        visitors.reduce((acc: any, v) => {
                            const city = (v.location?.located && v.location?.city) ? v.location.city : 'Unknown';
                            acc[city] = (acc[city] || 0) + 1;
                            return acc;
                        }, {})
                    )
                        .sort((a: any, b: any) => b[1] - a[1])
                        .slice(0, 8)
                        .map(([city, count]: any, i) => (
                            <div key={city} className="flex items-center justify-between py-2 border-b border-neutral-50 last:border-0 group hover:bg-neutral-50 px-2 rounded-lg transition-colors">
                                <div className="flex items-center gap-3">
                                    <span className="text-[10px] font-bold text-neutral-300">#0{i + 1}</span>
                                    <span className="text-xs font-bold text-neutral-700 uppercase tracking-wide group-hover:text-blue-600 transition-colors">{city}</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span className="text-xs font-bold text-neutral-900 tabular-nums">{count}</span>
                                    <div className="w-20 h-2 bg-neutral-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500 rounded-full shadow-[0_0_8px_rgba(37,99,235,0.2)]"
                                            style={{ width: `${(count / (visitors.length || 1)) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    {visitors.length === 0 && (
                        <div className="h-full flex flex-col items-center justify-center py-12 text-neutral-300 gap-4">
                            <div className="w-10 h-10 rounded-full border-2 border-dashed border-neutral-100 animate-spin" />
                            <span className="text-[10px] font-bold uppercase tracking-widest opacity-60">Scanning frequencies...</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Geolocation Health - New Card */}
            <GeolocationInsights visitors={visitors} />
        </div>
    );
};
